import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";

import { toast } from "react-toastify";
import { ethers } from "ethers";
import axios from "axios";

import useContract from "../hooks/useContract";
import CountdownTimer from "./CountdownTimer";

// =========================================================
// SWAP CONFIG
// =========================================================

const BNB = "BNB";

const TOKENS = {
  KIND: {
    symbol: "KIND",
    address: "0x41f52A42091A6B2146561bF05b722Ad1d0e46f8b",
    decimals: 18,
  },

  HUG: {
    symbol: "HUG",
    address: "0x62F3B222D7D8F607A56B5F417263c3C60D292908",
    decimals: 18,
  },
};

// PancakeSwap BSC router
const ROUTER_ADDRESS =
  "0x10ED43C718714eb63d5aA57B78B54704E256024E";

// WBNB
const WBNB_ADDRESS =
  "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";

// BSC RPC
const BSC_RPC =
  "https://bsc-dataseed.bnbchain.org";

// =========================================================
// ROUTER ABI
// =========================================================

const routerAbi = [
  "function getAmountsOut(uint amountIn, address[] calldata path) external view returns (uint[] memory)",

  "function swapExactETHForTokens(uint amountOutMin, address[] calldata path, address to, uint deadline) payable external",

  "function swapExactTokensForETHSupportingFeeOnTransferTokens(uint amountIn,uint amountOutMin,address[] calldata path,address to,uint deadline) external",

  "function swapExactTokensForTokensSupportingFeeOnTransferTokens(uint amountIn,uint amountOutMin,address[] calldata path,address to,uint deadline) external",
];

// =========================================================
// STAKE COMPONENT
// =========================================================

const StakeKIND = () => {
  // =======================================================
  // CONTRACT / WALLET
  // =======================================================

  const {
    address,
    isConnected,

    connectWallet,
    disconnectWallet,
    checkWalletConnection,

    getUserPositions,
    getReferralEarnings,
    stakeTokens,
    claimPosition,
    unstakePosition,

    getGlobalStats,
    getUserTotalClaimed,
    getUserTokenBalance,
    getUserClaimableRewards,
    getPositionCounter,

    getPendingKindRewards,
    claimKindRewards,
    getTotalKindClaimed,
  } = useContract();

  // =======================================================
  // WALLET PROVIDER
  // =======================================================

  const getEthereum = useCallback(() => {
    if (typeof window === "undefined") {
      return null;
    }

    // Prefer Bitget Wallet
    if (window.bitkeep?.ethereum) {
      return window.bitkeep.ethereum;
    }

    // Fallback to injected EVM wallet
    if (window.ethereum) {
      return window.ethereum;
    }

    return null;
  }, []);

  // =======================================================
  // CHECK WALLET ON LOAD
  // =======================================================

  useEffect(() => {
    checkWalletConnection();
  }, [checkWalletConnection]);

  // =======================================================
  // ACCOUNT CHANGE LISTENER
  // =======================================================

  useEffect(() => {
    const ethereum = getEthereum();

    if (!ethereum) {
      return;
    }

    const handleAccountsChanged = (accounts) => {
      if (accounts && accounts.length > 0) {
        checkWalletConnection();
      } else {
        disconnectWallet();
      }
    };

    ethereum.on?.(
      "accountsChanged",
      handleAccountsChanged
    );

    return () => {
      ethereum.removeListener?.(
        "accountsChanged",
        handleAccountsChanged
      );
    };
  }, [
    getEthereum,
    checkWalletConnection,
    disconnectWallet,
  ]);

  // =======================================================
  // STATE
  // =======================================================

  const [plans, setPlans] = useState([]);
  const [positions, setPositions] = useState([]);

  const [stakeAmount, setStakeAmount] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(0);

  const [referrer, setReferrer] = useState("");
  const [referralEarnings, setReferralEarnings] =
    useState("0");

  // Early Unstake Modal
  const [showEarlyPopup, setShowEarlyPopup] =
    useState(false);

  const [selectedPositionId, setSelectedPositionId] =
    useState(null);

  // Loading
  const [loadingStake, setLoadingStake] =
    useState(false);

  const [loadingClaim, setLoadingClaim] =
    useState({});

  const [loadingUnstake, setLoadingUnstake] =
    useState({});

  const [loadingCopy, setLoadingCopy] =
    useState(false);

  // KIND rewards
  const [pendingKind, setPendingKind] =
    useState(0);

  const [claimedKind, setClaimedKind] =
    useState(0);

  const [loadingKindClaim, setLoadingKindClaim] =
    useState(false);

  // =======================================================
  // SWAP STATE
  // =======================================================

  const [slippage, setSlippage] =
    useState(0.5);

  const [showSlippageModal, setShowSlippageModal] =
    useState(false);

  const [swapFrom, setSwapFrom] =
    useState(BNB);

  const [swapTo, setSwapTo] =
    useState("KIND");

  const [swapAmount, setSwapAmount] =
    useState("");

  const [swapEstimate, setSwapEstimate] =
    useState(0);

  const [swapLoading, setSwapLoading] =
    useState(false);

  // =======================================================
  // SWAP PATH
  // =======================================================

  const getSwapPath = useCallback(() => {
    // BNB -> TOKEN
    if (
      swapFrom === BNB &&
      swapTo !== BNB
    ) {
      return [
        WBNB_ADDRESS,
        TOKENS[swapTo].address,
      ];
    }

    // TOKEN -> BNB
    if (
      swapFrom !== BNB &&
      swapTo === BNB
    ) {
      return [
        TOKENS[swapFrom].address,
        WBNB_ADDRESS,
      ];
    }

    // TOKEN -> TOKEN
    if (
      swapFrom !== BNB &&
      swapTo !== BNB
    ) {
      return [
        TOKENS[swapFrom].address,
        WBNB_ADDRESS,
        TOKENS[swapTo].address,
      ];
    }

    return [];
  }, [swapFrom, swapTo]);

  // =======================================================
  // FETCH SWAP ESTIMATE
  // =======================================================

  const fetchSwapEstimate =
    useCallback(async () => {
      if (
        !swapAmount ||
        Number(swapAmount) <= 0
      ) {
        setSwapEstimate(0);
        return;
      }

      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const router =
          new ethers.Contract(
            ROUTER_ADDRESS,
            routerAbi,
            provider
          );

        const path =
          getSwapPath();

        if (path.length < 2) {
          setSwapEstimate(0);
          return;
        }

        const inputDecimals =
          swapFrom === BNB
            ? 18
            : TOKENS[swapFrom].decimals;

        const amountIn =
          ethers.parseUnits(
            swapAmount,
            inputDecimals
          );

        const amounts =
          await router.getAmountsOut(
            amountIn,
            path
          );

        const outputDecimals =
          swapTo === BNB
            ? 18
            : TOKENS[swapTo].decimals;

        const output =
          Number(
            ethers.formatUnits(
              amounts[amounts.length - 1],
              outputDecimals
            )
          );

        setSwapEstimate(output);
      } catch (err) {
        console.error(
          "Swap estimate error:",
          err
        );

        setSwapEstimate(0);
      }
    }, [
      swapAmount,
      swapFrom,
      swapTo,
      getSwapPath,
    ]);

  useEffect(() => {
    fetchSwapEstimate();
  }, [fetchSwapEstimate]);

  // =======================================================
  // ENSURE TOKEN ALLOWANCE
  // =======================================================

  const ensureAllowance =
    useCallback(
      async (
        signer,
        tokenAddress,
        owner,
        spender,
        amount
      ) => {
        const token =
          new ethers.Contract(
            tokenAddress,
            [
              "function allowance(address,address) view returns (uint256)",
              "function approve(address,uint256) returns (bool)",
            ],
            signer
          );

        const allowance =
          await token.allowance(
            owner,
            spender
          );

        if (allowance < amount) {
          const tx =
            await token.approve(
              spender,
              amount
            );

          await tx.wait();
        }
      },
      []
    );

  // =======================================================
  // HANDLE SWAP
  // =======================================================

  const handleSwap = async () => {
    if (!isConnected) {
      try {
        await connectWallet();
      } catch (err) {
        console.error(
          "Wallet connection failed:",
          err
        );

        toast.error(
          "Please connect your wallet"
        );

        return;
      }

      return;
    }

    if (
      !swapAmount ||
      Number(swapAmount) <= 0
    ) {
      toast.error(
        "Enter a valid amount"
      );

      return;
    }

    if (swapFrom === swapTo) {
      toast.error(
        "Select different tokens"
      );

      return;
    }

    if (!address) {
      toast.error(
        "Wallet address not found"
      );

      return;
    }

    setSwapLoading(true);

    try {
      const ethereum =
        getEthereum();

      if (!ethereum) {
        throw new Error(
          "Wallet provider not found"
        );
      }

      const provider =
        new ethers.BrowserProvider(
          ethereum
        );

      const signer =
        await provider.getSigner();

      const router =
        new ethers.Contract(
          ROUTER_ADDRESS,
          routerAbi,
          signer
        );

      const deadline =
        Math.floor(
          Date.now() / 1000
        ) + 600;

      const to = address;

      // =================================================
      // PATH
      // =================================================

      const path =
        getSwapPath();

      // =================================================
      // DECIMALS
      // =================================================

      const inputDecimals =
        swapFrom === BNB
          ? 18
          : TOKENS[swapFrom].decimals;

      const outputDecimals =
        swapTo === BNB
          ? 18
          : TOKENS[swapTo].decimals;

      const amountIn =
        ethers.parseUnits(
          swapAmount,
          inputDecimals
        );

      // =================================================
      // SLIPPAGE
      // =================================================

      let amountOutMin = 0n;

      if (
        swapEstimate > 0
      ) {
        const minimum =
          swapEstimate *
          (1 - slippage / 100);

        amountOutMin =
          ethers.parseUnits(
            minimum.toFixed(
              outputDecimals
            ),
            outputDecimals
          );
      }

      // =================================================
      // BNB -> TOKEN
      // =================================================

      if (swapFrom === BNB) {
        const tx =
          await router.swapExactETHForTokens(
            amountOutMin,
            path,
            to,
            deadline,
            {
              value: amountIn,
            }
          );

        toast.info(
          "Swap transaction submitted"
        );

        await tx.wait();
      }

      // =================================================
      // TOKEN -> BNB
      // =================================================

      else if (
        swapTo === BNB
      ) {
        await ensureAllowance(
          signer,
          TOKENS[swapFrom].address,
          address,
          ROUTER_ADDRESS,
          amountIn
        );

        const tx =
          await router.swapExactTokensForETHSupportingFeeOnTransferTokens(
            amountIn,
            0,
            path,
            to,
            deadline
          );

        toast.info(
          "Swap transaction submitted"
        );

        await tx.wait();
      }

      // =================================================
      // TOKEN -> TOKEN
      // =================================================

      else {
        await ensureAllowance(
          signer,
          TOKENS[swapFrom].address,
          address,
          ROUTER_ADDRESS,
          amountIn
        );

        const tx =
          await router.swapExactTokensForTokensSupportingFeeOnTransferTokens(
            amountIn,
            0,
            path,
            to,
            deadline
          );

        toast.info(
          "Swap transaction submitted"
        );

        await tx.wait();
      }

      toast.success(
        "Swap successful!"
      );

      setSwapAmount("");
      setSwapEstimate(0);

      await fetchSwapBalances();
    } catch (err) {
      console.error(
        "Swap failed:",
        err
      );

      const message =
        err?.reason ||
        err?.shortMessage ||
        err?.message ||
        "Swap failed";

      if (
        message
          .toLowerCase()
          .includes("user rejected")
      ) {
        toast.error(
          "Transaction rejected"
        );
      } else {
        toast.error(
          message.length > 100
            ? "Swap failed"
            : message
        );
      }
    } finally {
      setSwapLoading(false);
    }
  };

  // =======================================================
  // KIND REWARDS
  // =======================================================

  const fetchKindRewards =
    useCallback(async () => {
      if (!address) {
        setPendingKind(0);
        setClaimedKind(0);
        return;
      }

      try {
        const [
          pending,
          claimed,
        ] = await Promise.all([
          getPendingKindRewards(
            address
          ),
          getTotalKindClaimed(
            address
          ),
        ]);

        setPendingKind(
          Number(pending) || 0
        );

        setClaimedKind(
          Number(claimed) || 0
        );
      } catch (err) {
        console.error(
          "Error fetching KIND rewards:",
          err
        );
      }
    }, [
      address,
      getPendingKindRewards,
      getTotalKindClaimed,
    ]);

  // =======================================================
  // KIND PRICE
  // =======================================================

  const [kindPriceUSD, setKindPriceUSD] =
    useState(0);

  const KIND_ADDRESS =
    TOKENS.KIND.address;

  const KIND_BNB_PAIR =
    "0x628C0fe6DA9854EDf0f3E2C7657af6ecf29C740E";

  const pairAbi = [
    "function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32)",
    "function token0() view returns (address)",
    "function token1() view returns (address)",
  ];

  // =======================================================
  // FETCH KIND PRICE
  // =======================================================

  const fetchKindPrice =
    useCallback(async () => {
      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const pair =
          new ethers.Contract(
            KIND_BNB_PAIR,
            pairAbi,
            provider
          );

        const [
          reserves,
          token0,
        ] = await Promise.all([
          pair.getReserves(),
          pair.token0(),
        ]);

        let kindReserve;
        let bnbReserve;

        if (
          token0.toLowerCase() ===
          KIND_ADDRESS.toLowerCase()
        ) {
          kindReserve =
            reserves[0];

          bnbReserve =
            reserves[1];
        } else {
          kindReserve =
            reserves[1];

          bnbReserve =
            reserves[0];
        }

        const kindReserveNum =
          parseFloat(
            ethers.formatUnits(
              kindReserve,
              18
            )
          );

        const bnbReserveNum =
          parseFloat(
            ethers.formatUnits(
              bnbReserve,
              18
            )
          );

        if (
          kindReserveNum <= 0 ||
          bnbReserveNum <= 0
        ) {
          setKindPriceUSD(0);
          return;
        }

        const kindPerBNB =
          bnbReserveNum /
          kindReserveNum;

        // Chainlink BNB/USD
        const CHAINLINK_BNB_USD_FEED =
          "0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE";

        const aggregatorABI = [
          {
            name: "latestRoundData",
            inputs: [],
            outputs: [
              {
                name: "roundId",
                type: "uint80",
              },
              {
                name: "answer",
                type: "int256",
              },
              {
                name: "startedAt",
                type: "uint256",
              },
              {
                name: "updatedAt",
                type: "uint256",
              },
              {
                name: "answeredInRound",
                type: "uint80",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
          {
            name: "decimals",
            inputs: [],
            outputs: [
              {
                name: "",
                type: "uint8",
              },
            ],
            stateMutability: "view",
            type: "function",
          },
        ];

        const priceFeed =
          new ethers.Contract(
            CHAINLINK_BNB_USD_FEED,
            aggregatorABI,
            provider
          );

        const [
          roundData,
          decimals,
        ] = await Promise.all([
          priceFeed.latestRoundData(),
          priceFeed.decimals(),
        ]);

        const bnbUsd =
          parseFloat(
            ethers.formatUnits(
              roundData.answer,
              decimals
            )
          );

        const kindUsd =
          kindPerBNB *
          bnbUsd;

        setKindPriceUSD(
          kindUsd
        );
      } catch (err) {
        console.error(
          "Error fetching KIND price:",
          err
        );
      }
    }, []);

  useEffect(() => {
    fetchKindPrice();

    const interval =
      setInterval(
        fetchKindPrice,
        15000
      );

    return () =>
      clearInterval(interval);
  }, [fetchKindPrice]);

  // =======================================================
  // USER TOTALS
  // =======================================================

  const [userTotals, setUserTotals] =
    useState({
      personalStake: 0,
      claimableBaby: 0,
      claimableUSD: 0,
      claimedBaby: 0,
      claimedUSD: 0,
    });

  // =======================================================
  // GLOBAL TOTALS
  // =======================================================

  const [globalTotals, setGlobalTotals] =
    useState({
      totalPoolStake: 0,
      globalClaimableBaby: 0,
      globalClaimableUSD: 0,
      globalClaimedBaby: 0,
      globalClaimedUSD: 0,
    });

  const [
    userClaimedTotals,
    setUserClaimedTotals,
  ] = useState({
    totalUSD: "0",
    totalHUG: "0",
  });

  // =======================================================
  // PLANS
  // =======================================================

  const defaultPlans = [
    {
      id: 0,
      name: "FOUNDATION",
      lockDays: 90,
      monthlyRateBps: 1000,
      minAmount: 5000,
    },

    {
      id: 1,
      name: "FRAMING",
      lockDays: 90,
      monthlyRateBps: 1500,
      minAmount: 100000,
    },

    {
      id: 2,
      name: "ROOFING",
      lockDays: 90,
      monthlyRateBps: 2000,
      minAmount: 1000000,
    },

    {
      id: 3,
      name: "SKYSCRAPER",
      lockDays: 90,
      monthlyRateBps: 2500,
      minAmount: 20000000,
    },
  ];

  useEffect(() => {
    setPlans(
      defaultPlans
    );
  }, []);

  // =======================================================
  // REFERRER
  // =======================================================

  useEffect(() => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    const ref =
      params.get("ref");

    const zero =
      "0x0000000000000000000000000000000000000000";

    setReferrer(
      ref &&
        /^0x[a-fA-F0-9]{40}$/.test(
          ref
        )
        ? ref
        : zero
    );
  }, []);

  // =======================================================
  // FETCH POSITIONS
  // =======================================================

  const fetchPositions =
    useCallback(async () => {
      if (!isConnected) {
        setPositions([]);
        return;
      }

      try {
        const {
          positions: posData,
        } =
          await getUserPositions();

        const activePositions =
          posData.filter(
            (p) => !p.closed
          );

        const historyPositions =
          posData.filter(
            (p) =>
              !p.closed ||
              Number(
                p.claimableBaby || 0
              ) > 0
          );

        setPositions(
          historyPositions
        );

        const userClaimableBaby =
          activePositions.reduce(
            (sum, p) =>
              sum +
              (Number(
                p.claimableBaby
              ) || 0),
            0
          );

        const userClaimableUSD =
          activePositions.reduce(
            (sum, p) =>
              sum +
              (Number(
                p.claimableUSD
              ) || 0),
            0
          );

        const userClaimedBaby =
          activePositions.reduce(
            (sum, p) =>
              sum +
              (Number(
                p.paidBaby
              ) || 0),
            0
          );

        const userClaimedUSD =
          activePositions.reduce(
            (sum, p) =>
              sum +
              (Number(
                p.paidUSD
              ) || 0),
            0
          );

        const personalStake =
          activePositions.reduce(
            (sum, p) =>
              sum +
              Number(
                p.stakeAmount
              ),
            0
          );

        setUserTotals({
          personalStake:
            personalStake || 0,

          claimableBaby:
            userClaimableBaby,

          claimableUSD:
            userClaimableUSD,

          claimedBaby:
            userClaimedBaby,

          claimedUSD:
            userClaimedUSD,
        });
      } catch (err) {
        console.error(
          "Error fetching positions:",
          err
        );
      }
    }, [
      isConnected,
      getUserPositions,
    ]);

  // =======================================================
  // GLOBAL STATS
  // =======================================================

  const fetchGlobalStats =
    useCallback(async () => {
      try {
        const data =
          await getGlobalStats();

        if (!data) {
          return;
        }

        const {
          totalPoolStake,
          globalClaimableBaby,
          globalClaimableUSD,
          globalClaimedBaby,
          globalClaimedUSD,
        } = data;

        setGlobalTotals({
          totalPoolStake:
            Number(
              totalPoolStake
            ) || 0,

          globalClaimableBaby:
            Number(
              globalClaimableBaby
            ) || 0,

          globalClaimableUSD:
            Number(
              globalClaimableUSD
            ) || 0,

          globalClaimedBaby:
            Number(
              globalClaimedBaby
            ) || 0,

          globalClaimedUSD:
            Number(
              globalClaimedUSD
            ) || 0,
        });
      } catch (err) {
        console.error(
          "Error fetching global stats:",
          err
        );
      }
    }, [getGlobalStats]);

  // =======================================================
  // CLAIMABLE
  // =======================================================

  const [
    userClaimable,
    setUserClaimable,
  ] = useState({
    claimableHUG: 0,
    claimableUSD: 0,
  });

  useEffect(() => {
    const fetchClaimable =
      async () => {
        if (!address) {
          setUserClaimable({
            claimableHUG: 0,
            claimableUSD: 0,
          });

          return;
        }

        try {
          const data =
            await getUserClaimableRewards(
              address
            );

          setUserClaimable(
            data
          );
        } catch (err) {
          console.error(
            "Error fetching claimable:",
            err
          );
        }
      };

    fetchClaimable();
  }, [
    address,
    getUserClaimableRewards,
  ]);

  // =======================================================
  // CLAIMED TOTALS
  // =======================================================

  const fetchUserClaimedTotals =
    useCallback(async () => {
      if (
        !isConnected ||
        !address
      ) {
        return;
      }

      try {
        const totals =
          await getUserTotalClaimed(
            address
          );

        setUserClaimedTotals(
          totals
        );
      } catch (err) {
        console.error(
          "Error fetching total claimed:",
          err
        );
      }
    }, [
      isConnected,
      address,
      getUserTotalClaimed,
    ]);

  // =======================================================
  // REFERRAL
  // =======================================================

  const fetchReferral =
    useCallback(async () => {
      if (!isConnected) {
        setReferralEarnings(
          "0"
        );

        return;
      }

      try {
        const earnings =
          await getReferralEarnings();

        setReferralEarnings(
          earnings
        );
      } catch (err) {
        console.error(
          "Error fetching referral:",
          err
        );
      }
    }, [
      isConnected,
      getReferralEarnings,
    ]);

  // =======================================================
  // USER TOKEN BALANCE
  // =======================================================

  const [userBalance, setUserBalance] =
    useState(0);

  const fetchUserBalance =
    useCallback(async () => {
      if (
        !isConnected ||
        !address
      ) {
        setUserBalance(0);
        return;
      }

      try {
        const balance =
          await getUserTokenBalance(
            address
          );

        setUserBalance(
          Number(balance) || 0
        );
      } catch (err) {
        console.error(
          "Error fetching user balance:",
          err
        );
      }
    }, [
      isConnected,
      address,
      getUserTokenBalance,
    ]);

  // =======================================================
  // REFRESH DATA
  // =======================================================

  useEffect(() => {
    fetchGlobalStats();

    if (isConnected) {
      fetchPositions();
      fetchReferral();
      fetchUserClaimedTotals();
      fetchUserBalance();
      fetchKindRewards();
    }

    const interval =
      setInterval(() => {
        fetchGlobalStats();

        if (isConnected) {
          fetchPositions();
          fetchReferral();
          fetchUserClaimedTotals();
          fetchUserBalance();
          fetchKindRewards();
        }
      }, 15000);

    return () =>
      clearInterval(interval);
  }, [
    isConnected,
    fetchPositions,
    fetchReferral,
    fetchGlobalStats,
    fetchUserClaimedTotals,
    fetchUserBalance,
    fetchKindRewards,
  ]);

  // =======================================================
  // CLAIM KIND
  // =======================================================

  const handleClaimKind =
    async () => {
      if (
        pendingKind <= 0
      ) {
        return;
      }

      setLoadingKindClaim(
        true
      );

      try {
        await claimKindRewards();

        toast.success(
          "KIND rewards claimed!"
        );

        await fetchKindRewards();
      } catch (err) {
        console.error(err);

        toast.error(
          "KIND claim failed"
        );
      } finally {
        setLoadingKindClaim(
          false
        );
      }
    };

  // =======================================================
  // STAKE
  // =======================================================

  const handleStake =
    async () => {
      if (
        !stakeAmount ||
        Number(stakeAmount) <= 0
      ) {
        return;
      }

      if (!isConnected) {
        try {
          await connectWallet();
        } catch (err) {
          toast.error(
            "Please connect your wallet"
          );
        }

        return;
      }

      setLoadingStake(
        true
      );

      try {
        await stakeTokens(
          stakeAmount,
          selectedPlan,
          referrer
        );

        toast.success(
          "Stake successful!"
        );

        setStakeAmount("");

        await fetchPositions();
        await fetchGlobalStats();
        await fetchUserBalance();
      } catch (err) {
        console.error(
          err
        );

        toast.error(
          "Stake failed"
        );
      } finally {
        setLoadingStake(
          false
        );
      }
    };

  // =======================================================
  // CLAIM POSITION
  // =======================================================

  const handleClaim =
    async (positionId) => {
      setLoadingClaim(
        (prev) => ({
          ...prev,
          [positionId]: true,
        })
      );

      try {
        await claimPosition(
          positionId
        );

        toast.success(
          "Claim successful!"
        );

        await fetchPositions();
        await fetchGlobalStats();
      } catch (err) {
        console.error(
          err
        );

        toast.error(
          "Claim failed"
        );
      } finally {
        setLoadingClaim(
          (prev) => ({
            ...prev,
            [positionId]: false,
          })
        );
      }
    };

  // =======================================================
  // UNSTAKE
  // =======================================================

  const handleUnstake =
    async (positionId) => {
      setLoadingUnstake(
        (prev) => ({
          ...prev,
          [positionId]: true,
        })
      );

      try {
        await unstakePosition(
          positionId
        );

        toast.success(
          "Unstake successful!"
        );

        await fetchPositions();
        await fetchGlobalStats();
        await fetchUserBalance();
      } catch (err) {
        console.error(
          err
        );

        toast.error(
          "Unstake failed"
        );
      } finally {
        setLoadingUnstake(
          (prev) => ({
            ...prev,
            [positionId]: false,
          })
        );
      }
    };

  // =======================================================
  // COPY REFERRAL
  // =======================================================

  const handleCopy =
    async () => {
      if (!address) {
        return;
      }

      setLoadingCopy(
        true
      );

      try {
        const link =
          `${window.location.origin}?ref=${address}`;

        await navigator.clipboard
          .writeText(link);

        toast.success(
          "Referral link copied!"
        );
      } catch {
        toast.error(
          "Failed to copy link"
        );
      } finally {
        setLoadingCopy(
          false
        );
      }
    };

  // =======================================================
  // NUMBER FORMAT
  // =======================================================

  const formatLargeNumber =
    (num) => {
      if (num >= 1e9) {
        return (
          num / 1e9
        ).toFixed(2) + "B";
      }

      if (num >= 1e6) {
        return (
          num / 1e6
        ).toFixed(2) + "M";
      }

      if (num >= 1e3) {
        return (
          num / 1e3
        ).toFixed(2) + "K";
      }

      return Number(
        num || 0
      ).toFixed(2);
    };

  // =======================================================
  // HUG PRICE
  // =======================================================

  const [
    hugPriceUSD,
    setHugPriceUSD,
  ] = useState(0);

  const HUG_ADDRESS =
    TOKENS.HUG.address;

  const HUG_BNB_PAIR =
    "0x24AaAEdE44f1B96F5170eB18c22b371bBb1142b9";

  const fetchHugPrice =
    useCallback(async () => {
      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const pair =
          new ethers.Contract(
            HUG_BNB_PAIR,
            pairAbi,
            provider
          );

        const [
          reserves,
          token0,
        ] = await Promise.all([
          pair.getReserves(),
          pair.token0(),
        ]);

        let hugReserve;
        let bnbReserve;

        if (
          token0.toLowerCase() ===
          HUG_ADDRESS.toLowerCase()
        ) {
          hugReserve =
            reserves[0];

          bnbReserve =
            reserves[1];
        } else {
          hugReserve =
            reserves[1];

          bnbReserve =
            reserves[0];
        }

        const hugInBNB =
          Number(
            ethers.formatUnits(
              bnbReserve,
              18
            )
          ) /
          Number(
            ethers.formatUnits(
              hugReserve,
              18
            )
          );

        const bnbData =
          await axios.get(
            "https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd"
          );

        const bnbUsd =
          bnbData.data
            ?.binancecoin
            ?.usd || 0;

        setHugPriceUSD(
          hugInBNB *
            bnbUsd
        );
      } catch (err) {
        console.error(
          "Error fetching HUG price:",
          err
        );
      }
    }, []);

  useEffect(() => {
    fetchKindPrice();
    fetchHugPrice();

    const interval =
      setInterval(() => {
        fetchKindPrice();
        fetchHugPrice();
      }, 15000);

    return () =>
      clearInterval(interval);
  }, [
    fetchKindPrice,
    fetchHugPrice,
  ]);

  // =======================================================
  // POSITION COUNTER
  // =======================================================

  const [
    totalPositions,
    setTotalPositions,
  ] = useState(0);

  useEffect(() => {
    const loadCounter =
      async () => {
        const total =
          await getPositionCounter();

        setTotalPositions(
          Number(total) || 0
        );
      };

    loadCounter();

    const interval =
      setInterval(
        loadCounter,
        30000
      );

    return () =>
      clearInterval(interval);
  }, [
    getPositionCounter,
  ]);

  // =======================================================
  // TOKEN META
  // =======================================================

  const TOKEN_META = {
    BNB: {
      label: "BNB",
      logo: "bnb.png",
    },

    KIND: {
      label: "KIND",
      logo: "kindlogo.png",
    },

    HUG: {
      label: "HUG",
      logo: "hug.png",
    },
  };

  // =======================================================
  // TOKEN DROPDOWN
  // =======================================================

  const TokenDropdown = ({
    value,
    onChange,
  }) => {
    const [
      dropdownOpen,
      setDropdownOpen,
    ] = useState(false);

    return (
      <div className="relative flex-1">
        <button
          type="button"
          onClick={() =>
            setDropdownOpen(
              !dropdownOpen
            )
          }
          className="
            w-full flex items-center gap-2 px-3 py-2
            bg-black border border-[#d4af37] rounded
            text-white text-sm cursor-pointer
          "
        >
          <img
            src={
              TOKEN_META[value]
                .logo
            }
            className="w-5 h-5"
            alt={value}
          />

          <span>
            {value}
          </span>

          <span className="ml-auto text-xs">
            ▼
          </span>
        </button>

        {dropdownOpen && (
          <div
            className="
              absolute z-30 mt-1 w-full rounded
              border border-[#d4af37]
              bg-gradient-to-r from-[#5c3b00] to-[#ff9f1c]
              shadow-xl
            "
          >
            {Object.keys(
              TOKEN_META
            ).map((key) => (
              <div
                key={key}
                onClick={() => {
                  onChange(key);
                  setDropdownOpen(
                    false
                  );
                }}
                className="
                  flex items-center gap-2 px-3 py-2
                  text-white text-sm cursor-pointer
                  hover:bg-black/30
                "
              >
                <img
                  src={
                    TOKEN_META[key]
                      .logo
                  }
                  className="w-5 h-5"
                  alt={key}
                />

                {
                  TOKEN_META[key]
                    .label
                }
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // =======================================================
  // BNB PRICE
  // =======================================================

  const [
    bnbPriceUSD,
    setBnbPriceUSD,
  ] = useState(0);

  useEffect(() => {
    const fetchBnbPrice =
      async () => {
        try {
          const res =
            await fetch(
              "https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd"
            );

          const data =
            await res.json();

          setBnbPriceUSD(
            data?.binancecoin
              ?.usd || 0
          );
        } catch (err) {
          console.error(
            "BNB price fetch failed",
            err
          );

          setBnbPriceUSD(0);
        }
      };

    fetchBnbPrice();

    const interval =
      setInterval(
        fetchBnbPrice,
        30000
      );

    return () =>
      clearInterval(interval);
  }, []);

  // =======================================================
  // SWAP USD
  // =======================================================

  const swapUsd =
    useMemo(() => {
      if (
        !swapEstimate ||
        swapEstimate <= 0
      ) {
        return 0;
      }

      if (
        swapTo === "KIND" &&
        kindPriceUSD > 0
      ) {
        return (
          swapEstimate *
          kindPriceUSD
        );
      }

      if (
        swapTo === "HUG" &&
        hugPriceUSD > 0
      ) {
        return (
          swapEstimate *
          hugPriceUSD
        );
      }

      if (
        swapTo === "BNB" &&
        bnbPriceUSD > 0
      ) {
        return (
          swapEstimate *
          bnbPriceUSD
        );
      }

      return 0;
    }, [
      swapEstimate,
      swapTo,
      kindPriceUSD,
      hugPriceUSD,
      bnbPriceUSD,
    ]);

  // =======================================================
  // SUPPLY
  // =======================================================

  const BURN_WALLETS = [
    "0x0000000000000000000000000000000000000000",
    "0x000000000000000000000000000000000000dEaD",
  ];

  const erc20Abi = [
    "function totalSupply() view returns (uint256)",
    "function balanceOf(address) view returns (uint256)",
    "function decimals() view returns (uint8)",
  ];

  const [
    totalSupply,
    setTotalSupply,
  ] = useState(0);

  const [
    circulatingSupply,
    setCirculatingSupply,
  ] = useState(0);

  const [
    totalKindBurned,
    setTotalKindBurned,
  ] = useState(0);

  const fetchTotalKindBurned =
    useCallback(async () => {
      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const token =
          new ethers.Contract(
            KIND_ADDRESS,
            erc20Abi,
            provider
          );

        const decimals =
          await token.decimals();

        const balances =
          await Promise.all(
            BURN_WALLETS.map(
              (addr) =>
                token.balanceOf(
                  addr
                )
            )
          );

        const totalBurnedRaw =
          balances.reduce(
            (
              sum,
              bal
            ) =>
              sum + bal,
            0n
          );

        const totalBurned =
          Number(
            ethers.formatUnits(
              totalBurnedRaw,
              decimals
            )
          );

        setTotalKindBurned(
          totalBurned
        );
      } catch (err) {
        console.error(
          "Error fetching total burned KIND:",
          err
        );

        setTotalKindBurned(
          0
        );
      }
    }, []);

  const fetchSupplyData =
    useCallback(async () => {
      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const token =
          new ethers.Contract(
            KIND_ADDRESS,
            erc20Abi,
            provider
          );

        const decimals =
          await token.decimals();

        const totalSupplyRaw =
          await token.totalSupply();

        const burnedBalances =
          await Promise.all(
            BURN_WALLETS.map(
              (addr) =>
                token.balanceOf(
                  addr
                )
            )
          );

        const burnedRaw =
          burnedBalances.reduce(
            (
              sum,
              bal
            ) =>
              sum + bal,
            0n
          );

        const totalSupplyNum =
          Number(
            ethers.formatUnits(
              totalSupplyRaw,
              decimals
            )
          );

        const burnedNum =
          Number(
            ethers.formatUnits(
              burnedRaw,
              decimals
            )
          );

        const circulating =
          totalSupplyNum -
          burnedNum;

        setTotalSupply(
          totalSupplyNum
        );

        setTotalKindBurned(
          burnedNum
        );

        setCirculatingSupply(
          circulating
        );
      } catch (err) {
        console.error(
          "Error fetching supply data:",
          err
        );
      }
    }, []);

  useEffect(() => {
    fetchTotalKindBurned();

    const interval =
      setInterval(
        fetchTotalKindBurned,
        30000
      );

    return () =>
      clearInterval(interval);
  }, [
    fetchTotalKindBurned,
  ]);

  useEffect(() => {
    fetchSupplyData();

    const interval =
      setInterval(
        fetchSupplyData,
        60000
      );

    return () =>
      clearInterval(interval);
  }, [
    fetchSupplyData,
  ]);

  // =======================================================
  // SLIPPAGE ESCAPE
  // =======================================================

  useEffect(() => {
    const esc = (e) => {
      if (
        e.key === "Escape"
      ) {
        setShowSlippageModal(
          false
        );
      }
    };

    window.addEventListener(
      "keydown",
      esc
    );

    return () =>
      window.removeEventListener(
        "keydown",
        esc
      );
  }, []);

  // =======================================================
  // SUPPLY PERCENTAGES
  // =======================================================

  const burnedPercent =
    totalSupply > 0
      ? (totalKindBurned /
          totalSupply) *
        100
      : 0;

  const circulatingPercent =
    totalSupply > 0
      ? (circulatingSupply /
          totalSupply) *
        100
      : 0;

  // =======================================================
  // SWAP BALANCES
  // =======================================================

  const [
    swapBalances,
    setSwapBalances,
  ] = useState({
    BNB: 0,
    KIND: 0,
    HUG: 0,
  });

  const fetchSwapBalances =
    useCallback(async () => {
      if (
        !isConnected ||
        !address
      ) {
        setSwapBalances({
          BNB: 0,
          KIND: 0,
          HUG: 0,
        });

        return;
      }

      try {
        const provider =
          new ethers.JsonRpcProvider(
            BSC_RPC
          );

        const bnbBalance =
          await provider.getBalance(
            address
          );

        const erc20 =
          (token) =>
            new ethers.Contract(
              TOKENS[token]
                .address,
              [
                "function balanceOf(address) view returns (uint256)",
                "function decimals() view returns (uint8)",
              ],
              provider
            );

        const kindContract =
          erc20("KIND");

        const hugContract =
          erc20("HUG");

        const [
          kindBal,
          hugBal,
          kindDecimals,
          hugDecimals,
        ] =
          await Promise.all([
            kindContract.balanceOf(
              address
            ),

            hugContract.balanceOf(
              address
            ),

            kindContract.decimals(),

            hugContract.decimals(),
          ]);

        setSwapBalances({
          BNB: Number(
            ethers.formatEther(
              bnbBalance
            )
          ),

          KIND: Number(
            ethers.formatUnits(
              kindBal,
              kindDecimals
            )
          ),

          HUG: Number(
            ethers.formatUnits(
              hugBal,
              hugDecimals
            )
          ),
        });
      } catch (err) {
        console.error(
          "Error fetching swap balances:",
          err
        );
      }
    }, [
      isConnected,
      address,
    ]);

  useEffect(() => {
    fetchSwapBalances();

    const interval =
      setInterval(
        fetchSwapBalances,
        15000
      );

    return () =>
      clearInterval(interval);
  }, [
    fetchSwapBalances,
  ]);

  // =======================================================
  // SWAP TOKEN VALIDATION
  // =======================================================

  useEffect(() => {
    if (
      swapFrom ===
      swapTo
    ) {
      setSwapTo(
        swapFrom === BNB
          ? "KIND"
          : BNB
      );
    }
  }, [
    swapFrom,
    swapTo,
  ]);

  // =======================================================
  // STAKE VALIDATION
  // =======================================================

  const selectedPlanData =
    plans.find(
      (p) =>
        p.id ===
        selectedPlan
    );

  const minRequired =
    selectedPlanData
      ?.minAmount || 0;

  const stakeAmountNum =
    Number(
      stakeAmount || 0
    );

  const isStakeDisabled =
    loadingStake ||
    !stakeAmount ||
    stakeAmountNum <= 0 ||
    stakeAmountNum <
      minRequired ||
    !isConnected;

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div className="min-h-screen text-white font-sans px-2 sm:px-4 py-10">

      {/* =====================================================
          STAKE + SWAP
      ====================================================== */}

      <div className="max-w-[1280px] mx-auto flex flex-col md:flex-row gap-6 mb-8">

        {/* =================================================
            STAKE BOX
        ================================================= */}

        <div className="flex-1 bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] flex flex-col gap-4 shadow-[0_0_15px_#d4af3755]">

          <h2 className="text-[#d4af37] text-xl font-bold flex justify-between items-center">
            Stake KIND

            <span className="text-sm text-gray-300">
              Balance:{" "}
              {formatLargeNumber(
                userBalance
              )}{" "}
              KIND

              {kindPriceUSD >
                0 && (
                <span className="text-gray-400">
                  {" "}
                  ($
                  {(
                    userBalance *
                    kindPriceUSD
                  ).toFixed(2)}{" "}
                  USD)
                </span>
              )}
            </span>
          </h2>

          <div className="text-sm text-gray-400">
            KIND Price:{" "}
            {kindPriceUSD >
            0
              ? `$${kindPriceUSD.toFixed(
                  6
                )}`
              : "Loading..."}
          </div>

          {!isConnected && (
            <button
              onClick={
                connectWallet
              }
              className="w-full px-6 py-3 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white font-semibold rounded-full hover:brightness-110"
            >
              Connect Wallet
            </button>
          )}

          <div className="relative w-full">
            <input
              type="number"
              placeholder="Amount to stake"
              value={
                stakeAmount
              }
              onChange={(e) =>
                setStakeAmount(
                  e.target.value
                )
              }
              className="w-full p-3 pr-16 rounded-md bg-black border border-[#d4af37] text-white placeholder:text-gray-400"
            />

            <button
              onClick={() =>
                setStakeAmount(
                  userBalance.toString()
                )
              }
              disabled={
                !isConnected
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#d4af37] text-black rounded hover:brightness-110 text-sm disabled:opacity-50"
            >
              Max
            </button>
          </div>

          {/* PLANS */}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">

            {plans.map(
              (plan) => (
                <button
                  key={
                    plan.id
                  }
                  onClick={() =>
                    setSelectedPlan(
                      plan.id
                    )
                  }
                  className={`flex flex-col justify-center items-center p-5 rounded-xl border-2 transition-all text-center ${
                    selectedPlan ===
                    plan.id
                      ? "border-[#d4af37] bg-gradient-to-br from-[#d4af37] via-[#b8860b] to-[#1a1a1a]"
                      : "border-[#b8860b] bg-[#1a1a1a]"
                  } hover:brightness-110`}
                >
                  <div className="text-xl font-bold text-white">
                    {
                      plan.name
                    }
                  </div>

                  <div className="text-sm text-gray-300 mt-2">
                    {
                      plan.lockDays
                    }{" "}
                    Days Lock
                  </div>

                  <div className="text-lg font-semibold text-[#d4af37] mt-2">
                    {(
                      plan.monthlyRateBps /
                      100
                    ).toFixed(
                      1
                    )}
                    % Monthly
                  </div>

                  <div className="text-sm text-gray-400 mt-1">
                    Min:{" "}
                    {plan.minAmount.toLocaleString()}{" "}
                    KIND
                  </div>
                </button>
              )
            )}

          </div>

          <button
            onClick={
              handleStake
            }
            disabled={
              isStakeDisabled
            }
            className={`
              w-full px-6 py-3
              bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a]
              text-white font-semibold rounded-full
              transition-all
              ${
                isStakeDisabled
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:brightness-110"
              }
            `}
          >
            {!isConnected
              ? "Connect Wallet"
              : loadingStake
              ? "Processing…"
              : "Stake Now"}
          </button>

        </div>

        {/* =================================================
            SWAP BOX
        ================================================= */}

        <div className="flex-1 bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] flex flex-col gap-4 shadow-[0_0_15px_#d4af3755]">

          <div className="flex items-center justify-between">

            <h2 className="text-[#d4af37] text-xl font-bold">
              Swap Tokens
            </h2>

            <div className="flex items-center gap-4 text-sm text-gray-400">
              <span>
                Balance:

                <span className="text-[#d4af37] ml-1">
                  {Number(
                    swapBalances[
                      swapFrom
                    ] || 0
                  ).toFixed(
                    4
                  )}{" "}
                  {
                    swapFrom
                  }
                </span>
              </span>
            </div>

          </div>

          {/* FROM / TO */}

          <div className="flex gap-2">
            <TokenDropdown
              value={
                swapFrom
              }
              onChange={
                setSwapFrom
              }
            />

            <TokenDropdown
              value={
                swapTo
              }
              onChange={
                setSwapTo
              }
            />
          </div>

          {/* AMOUNT */}

          <div className="relative w-full">

            <input
              type="number"
              placeholder={`Amount in ${swapFrom}`}
              value={
                swapAmount
              }
              onChange={(e) =>
                setSwapAmount(
                  e.target.value
                )
              }
              className="w-full p-2 pr-14 rounded-md bg-black border border-[#d4af37] text-white text-sm"
            />

            <button
              onClick={() =>
                setSwapAmount(
                  swapBalances[
                    swapFrom
                  ]?.toString() ||
                    ""
                )
              }
              disabled={
                !isConnected
              }
              className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-[#d4af37] text-black rounded hover:brightness-110 text-sm disabled:opacity-50"
            >
              Max
            </button>

          </div>

          {/* ESTIMATE */}

          <div className="flex items-center justify-between text-sm text-gray-400">

            <div>
              You receive{" "}

              <span className="text-[#d4af37] font-semibold">
                {swapEstimate >
                0
                  ? swapEstimate.toFixed(
                      6
                    )
                  : "0.000000"}{" "}
                {
                  swapTo
                }
              </span>

              <span className="ml-2 text-gray-500">
                {bnbPriceUSD ===
                  0 &&
                swapTo ===
                  "BNB"
                  ? "(loading USD...)"
                  : swapUsd >
                    0
                  ? `(~$${swapUsd.toFixed(
                      2
                    )})`
                  : ""}
              </span>
            </div>

            <button
              onClick={() =>
                setShowSlippageModal(
                  true
                )
              }
              className="flex items-center gap-2 text-[#d4af37] hover:opacity-80"
              title="Swap Settings"
            >
              <span className="text-xs font-semibold">
                Slippage:
              </span>

              <span
                className={`text-xs font-semibold ${
                  slippage >
                  5
                    ? "text-red-400"
                    : "text-[#d4af37]"
                }`}
              >
                {
                  slippage
                }
                %
              </span>

              <span>
                ⚙️
              </span>
            </button>

          </div>

          {/* SWAP BUTTON */}

          <button
            onClick={
              handleSwap
            }
            disabled={
              swapLoading
            }
            className="mt-2 px-4 py-2 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white font-semibold rounded-full hover:brightness-110 disabled:opacity-50"
          >
            {swapLoading
              ? "Processing…"
              : !isConnected
              ? "Connect Wallet"
              : "Swap"}
          </button>

          {/* REFERRAL */}

          <div className="bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15] flex flex-col gap-1">

            <div className="text-[#d4af37] font-semibold text-sm">
              Referral Earnings
            </div>

            <div className="text-white font-bold text-xl">

              {isConnected
                ? Number(
                    referralEarnings ||
                      0
                  ).toFixed(
                    2
                  )
                : "0.00"}{" "}
              HUG

              <span className="text-gray-400 font-normal">

                {" "}
                ($
                {isConnected &&
                hugPriceUSD >
                  0
                  ? (
                      Number(
                        referralEarnings ||
                          0
                      ) *
                      hugPriceUSD
                    ).toFixed(
                      2
                    )
                  : "0.00"}{" "}
                USD)

              </span>

            </div>
          </div>

          {/* KIND REWARDS */}

          <div className="bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15] flex flex-col gap-2">

            <div className="text-[#d4af37] font-semibold text-sm">
              KIND Rewards (Airdrop)
            </div>

            <div className="text-white font-bold text-xl">

              {pendingKind.toFixed(
                4
              )}{" "}
              KIND

              {kindPriceUSD >
                0 && (
                <span className="text-gray-400 text-xs ml-2">
                  ($
                  {(
                    pendingKind *
                    kindPriceUSD
                  ).toFixed(
                    2
                  )}{" "}
                  USD)
                </span>
              )}

            </div>

            <div className="text-gray-400 text-xs">

              Total Claimed:{" "}
              {claimedKind.toFixed(
                4
              )}{" "}
              KIND

              {kindPriceUSD >
                0 && (
                <span className="ml-2">
                  ($
                  {(
                    claimedKind *
                    kindPriceUSD
                  ).toFixed(
                    2
                  )}{" "}
                  USD)
                </span>
              )}

            </div>

            <button
              onClick={
                handleClaimKind
              }
              disabled={
                pendingKind ===
                  0 ||
                loadingKindClaim ||
                !isConnected
              }
              className="mt-1 px-3 py-1.5 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white font-semibold rounded-full hover:brightness-110 disabled:opacity-50 text-sm"
            >
              {loadingKindClaim
                ? "Processing…"
                : "Claim KIND"}
            </button>

          </div>

          {/* REFERRAL LINK */}

          <div className="mt-0">

            <div className="text-gray-300 mb-2">
              Share your referral link:
            </div>

            <div className="flex gap-2">

              <input
                type="text"
                readOnly
                value={
                  isConnected
                    ? `${window.location.origin}?ref=${address}`
                    : "Connect Wallet to Get Link"
                }
                className={`w-full p-3 rounded-md bg-black border border-[#d4af37] text-[#fff8e8] ${
                  isConnected
                    ? "cursor-text"
                    : "cursor-not-allowed"
                }`}
                onClick={(e) =>
                  isConnected &&
                  e.target.select()
                }
              />

              {isConnected && (
                <button
                  onClick={
                    handleCopy
                  }
                  disabled={
                    loadingCopy
                  }
                  className="px-4 py-3 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-[#fff8e8] font-bold rounded-md hover:brightness-110 disabled:opacity-50"
                >
                  {loadingCopy
                    ? "..."
                    : "Copy"}
                </button>
              )}

            </div>
          </div>

        </div>
      </div>

      {/* =====================================================
          SLIPPAGE MODAL
      ====================================================== */}

      {showSlippageModal && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
          onClick={() =>
            setShowSlippageModal(
              false
            )
          }
        >
          <div
            className="bg-[#2a1f0f] border border-[#d4af37] rounded-xl p-6 w-[320px] shadow-xl"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="flex justify-between items-center mb-4">

              <h3 className="text-[#d4af37] font-bold text-lg">
                Swap Settings
              </h3>

              <button
                onClick={() =>
                  setShowSlippageModal(
                    false
                  )
                }
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>

            </div>

            <div className="mb-3">

              <div className="text-sm text-gray-300 mb-2">
                Slippage Tolerance
              </div>

              <div className="flex gap-2 mb-2">

                {[
                  0.1,
                  0.5,
                  1,
                  3,
                ].map(
                  (v) => (
                    <button
                      key={v}
                      onClick={() =>
                        setSlippage(
                          v
                        )
                      }
                      className={`px-3 py-1 rounded text-xs border ${
                        slippage ===
                        v
                          ? "border-[#d4af37] text-[#d4af37]"
                          : "border-gray-600 text-gray-400"
                      }`}
                    >
                      {v}%
                    </button>
                  )
                )}

              </div>

              <input
                type="number"
                min="0"
                step="0.1"
                value={
                  slippage
                }
                onChange={(e) =>
                  setSlippage(
                    Number(
                      e.target
                        .value
                    )
                  )
                }
                className="w-full p-2 rounded bg-black border border-[#d4af37] text-white text-sm"
              />

            </div>

            <button
              onClick={() =>
                setShowSlippageModal(
                  false
                )
              }
              className="mt-4 w-full px-4 py-2 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white font-semibold rounded-full hover:brightness-110"
            >
              Save Settings
            </button>

          </div>
        </div>
      )}

      {/* =====================================================
          USER TOTALS
      ====================================================== */}

      <div className="bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] max-w-[1280px] mx-auto mb-6 shadow-[0_0_10px_#d4af3720]">

        <h2 className="text-[#d4af37] text-xl font-bold mb-4">
          👤 Your Totals
        </h2>

        <div className="flex flex-col md:flex-row justify-between gap-4">

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Personal Stake
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {userTotals.personalStake.toFixed(
                2
              )}{" "}
              KIND

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {(
                  userTotals.personalStake *
                  (kindPriceUSD ||
                    0)
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Claimable HUG
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {Number(
                userClaimable.claimableHUG ||
                  0
              ).toFixed(
                2
              )}{" "}
              HUG

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {Number(
                  userClaimable.claimableUSD ||
                    0
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Total Claimed
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {Number(
                userClaimedTotals.totalHUG
              ).toFixed(
                2
              )}{" "}
              HUG

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {Number(
                  userClaimedTotals.totalUSD
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

        </div>
      </div>

      {/* =====================================================
          GLOBAL TOTALS
      ====================================================== */}

      <div className="bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] max-w-[1280px] mx-auto mb-6 shadow-[0_0_10px_#d4af3720]">

        <h2 className="text-[#d4af37] text-xl font-bold mb-4 flex flex-wrap items-center gap-3">

          🌍 Global Totals

          <span className="font-bold text-[#d4af37] text-lg">
            — Total Stakers Positions:{" "}
            {
              totalPositions
            }
          </span>

        </h2>

        <div className="flex flex-col md:flex-row justify-between gap-4">

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Total Pool Stake
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {globalTotals.totalPoolStake.toFixed(
                2
              )}{" "}
              KIND

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {(
                  globalTotals.totalPoolStake *
                  (kindPriceUSD ||
                    0)
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Global Claimable HUG
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {globalTotals.globalClaimableBaby.toFixed(
                2
              )}{" "}
              HUG

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {globalTotals.globalClaimableUSD.toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Global Claimed HUG
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {globalTotals.globalClaimedBaby.toFixed(
                2
              )}{" "}
              HUG

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                ($
                {globalTotals.globalClaimedUSD.toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

        </div>
      </div>

      {/* =====================================================
          SUPPLY
      ====================================================== */}

      <div className="bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] max-w-[1280px] mx-auto mb-6 shadow-[0_0_10px_#d4af3720]">

        <h2 className="text-[#d4af37] text-xl font-bold mb-4">
          📦 Supply Overview
        </h2>

        <div className="flex flex-col md:flex-row justify-between gap-4">

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Total Supply
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {totalSupply.toFixed(
                2
              )}{" "}
              KIND

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                (100% • $
                {(
                  totalSupply *
                  (kindPriceUSD ||
                    0)
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1">
              Circulating Supply
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {circulatingSupply.toFixed(
                2
              )}{" "}
              KIND

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                (
                {circulatingPercent.toFixed(
                  2
                )}
                % • $
                {(
                  circulatingSupply *
                  (kindPriceUSD ||
                    0)
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

          <div className="flex-1 bg-[#1e160b]/60 p-3 rounded-lg border border-[#3a2c15]">

            <div className="text-gray-400 capitalize text-sm mb-1 flex items-center gap-1">
              🔥 Total Burned
            </div>

            <div className="text-white font-bold text-xl sm:text-2xl">

              {totalKindBurned.toFixed(
                2
              )}{" "}
              KIND

              <span className="text-gray-400 text-base sm:text-lg ml-1">
                (
                {burnedPercent.toFixed(
                  2
                )}
                % • $
                {(
                  totalKindBurned *
                  (kindPriceUSD ||
                    0)
                ).toFixed(
                  2
                )}{" "}
                USD)
              </span>

            </div>
          </div>

        </div>
      </div>

      {/* =====================================================
          STAKING HISTORY
      ====================================================== */}

      <div className="bg-[#2a1f0f] p-4 sm:p-6 rounded-xl border border-[#d4af37] max-w-[1280px] mx-auto shadow-[0_0_10px_#d4af3720] relative">

        <h2 className="text-[#d4af37] text-xl font-bold mb-4">
          📜 Staking History
        </h2>

        <div className="overflow-x-auto">

          <table className="w-full text-left border-collapse">

            <thead>
              <tr className="border-b border-[#d4af37] text-sm sm:text-base">

                <th className="p-2">
                  Amount
                </th>

                <th className="p-2">
                  Lock Duration
                </th>

                <th className="p-2">
                  Fixed Returns
                </th>

                <th className="p-2">
                  Start Time
                </th>

                <th className="p-2">
                  End Time
                </th>

                <th className="p-2">
                  Status
                </th>

                <th className="p-2">
                  Actions
                </th>

              </tr>
            </thead>

            <tbody>

              {positions.length ===
                0 && (
                <tr>
                  <td
                    colSpan={
                      7
                    }
                    className="text-center p-4 text-gray-400 text-sm sm:text-base"
                  >
                    No stakes found
                  </td>
                </tr>
              )}

              {positions.map(
                (pos) => {
                  const endTime =
                    Number(
                      pos.endTime
                    );

                  const startTime =
                    Number(
                      pos.startTime
                    );

                  const isEarly =
                    endTime >
                    Math.floor(
                      Date.now() /
                        1000
                    );

                  return (
                    <tr
                      key={
                        pos.id
                      }
                      className="border-b border-[#d4af37] text-xs sm:text-sm"
                    >

                      {/* Amount */}

                      <td className="p-2">

                        {(
                          Number(
                            pos.stakeAmount
                          ) || 0
                        ).toFixed(
                          2
                        )}{" "}
                        KIND

                        {kindPriceUSD >
                          0 && (
                          <div className="text-xs text-gray-400">
                            ($
                            {(
                              (Number(
                                pos.stakeAmount
                              ) ||
                                0) *
                              kindPriceUSD
                            ).toFixed(
                              2
                            )}{" "}
                            USD)
                          </div>
                        )}

                      </td>

                      {/* Lock */}

                      <td className="p-2">
                        {
                          pos.lockDays
                        }{" "}
                        days
                      </td>

                      {/* Returns */}

                      <td className="p-2">

                        {(
                          Number(
                            pos.fixedReturns
                          ) || 0
                        ).toFixed(
                          2
                        )}{" "}
                        HUG

                        {pos.fixedReturnsUSD && (
                          <span className="text-xs text-gray-400">
                            {" "}
                            ($
                            {Number(
                              pos.fixedReturnsUSD
                            ).toFixed(
                              2
                            )}{" "}
                            USD)
                          </span>
                        )}

                      </td>

                      {/* Start */}

                      <td className="p-2 text-gray-300 text-xs sm:text-sm">

                        {startTime >
                        0
                          ? new Date(
                              startTime *
                                1000
                            ).toLocaleString()
                          : "Invalid Date"}

                      </td>

                      {/* End */}

                      <td className="p-2">

                        {endTime >
                        Math.floor(
                          Date.now() /
                            1000
                        ) ? (
                          <div className="flex flex-col items-start gap-1">

                            <span className="text-gray-300 text-xs">

                              Ends in:{" "}

                              <span className="text-gray-400 text-[11px]">
                                (
                                {new Date(
                                  endTime *
                                    1000
                                ).toLocaleString()}
                                )
                              </span>

                            </span>

                            <CountdownTimer
                              nextClaimTimestamp={
                                endTime
                              }
                            />

                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">

                            Ended
                            <br />

                            <span className="text-[11px]">

                              {endTime >
                              0
                                ? new Date(
                                    endTime *
                                      1000
                                  ).toLocaleString()
                                : "Invalid Date"}

                            </span>

                          </span>
                        )}

                      </td>

                      {/* Status */}

                      <td className="p-2">

                        {!pos.closed
                          ? "Active"
                          : Number(
                              pos.claimableBaby ||
                                0
                            ) >
                            0
                          ? "Closed (Claimable)"
                          : "Closed"}

                      </td>

                      {/* Actions */}

                      <td className="p-2 flex flex-wrap gap-2 items-center">

                        {/* Claim */}

                        <button
                          onClick={() =>
                            handleClaim(
                              pos.id
                            )
                          }
                          disabled={
                            Number(
                              pos.claimableBaby ||
                                0
                            ) ===
                              0 ||
                            loadingClaim[
                              pos.id
                            ]
                          }
                          className="px-3 py-1 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white rounded-full font-semibold hover:brightness-110 text-sm disabled:opacity-50"
                        >
                          {loadingClaim[
                            pos.id
                          ]
                            ? "Processing…"
                            : "Claim"}
                        </button>

                        {/* Unstake */}

                        <button
                          onClick={() => {
                            if (
                              isEarly
                            ) {
                              setSelectedPositionId(
                                pos.id
                              );

                              setShowEarlyPopup(
                                true
                              );
                            } else {
                              handleUnstake(
                                pos.id
                              );
                            }
                          }}
                          disabled={
                            loadingUnstake[
                              pos.id
                            ]
                          }
                          className="px-3 py-1 bg-[#1a1a1a] text-[#d4af37] border border-[#d4af37] rounded-full font-semibold hover:brightness-110 text-sm relative flex items-center gap-1 disabled:opacity-50"
                        >
                          {loadingUnstake[
                            pos.id
                          ]
                            ? "Processing…"
                            : "Unstake"}

                          {isEarly && (
                            <span className="ml-1 text-yellow-400 text-xs">
                              ⚠
                            </span>
                          )}

                        </button>

                      </td>

                    </tr>
                  );
                }
              )}

            </tbody>
          </table>
        </div>

        {/* =================================================
            EARLY UNSTAKE POPUP
        ================================================== */}

        {showEarlyPopup && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50">

            <div className="bg-[#2a1f0f] border border-[#d4af37] p-6 rounded-xl max-w-sm text-center shadow-lg">

              <h3 className="text-[#d4af37] text-lg font-bold mb-2">
                Early Unstake Warning ⚠
              </h3>

              <p className="text-gray-300 text-sm mb-4">

                Unstaking before the
                end date will incur a{" "}

                <span className="text-[#d4af37] font-semibold">
                  10% penalty
                </span>{" "}

                sent to the burn wallet.

              </p>

              <div className="flex justify-center gap-4">

                <button
                  onClick={() =>
                    setShowEarlyPopup(
                      false
                    )
                  }
                  className="px-4 py-2 bg-[#1a1a1a] border border-[#d4af37] text-[#d4af37] rounded-lg font-semibold hover:brightness-110"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    handleUnstake(
                      selectedPositionId
                    );

                    setShowEarlyPopup(
                      false
                    );
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#d4af37] via-[#b8860b] to-[#1a1a1a] text-white rounded-lg font-semibold hover:brightness-110"
                >
                  Confirm Unstake
                </button>

              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StakeKIND;